import { type ReactNode, useMemo } from "react";
import {
    ConnectionProvider,
    WalletProvider,
} from "@solana/wallet-adapter-react";
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";
import { clusterApiUrl } from "@solana/web3.js";

import "@solana/wallet-adapter-react-ui/styles.css";

/**
 * T2.1 — Solana wallet context provider.
 *
 * Wraps children with ConnectionProvider (RPC), WalletProvider, and
 * WalletModalProvider (wallet selection modal). Auto-detects all
 * installed wallets via the Wallet Standard protocol.
 */
interface SolanaProviderProps {
    children: ReactNode;
    /** Solana cluster — defaults to "devnet" */
    network?: "devnet" | "testnet" | "mainnet-beta";
}

export default function SolanaProvider({
    children,
    network = "devnet",
}: SolanaProviderProps): ReactNode {
    const endpoint = useMemo(
        () => import.meta.env.VITE_SOLANA_RPC_URL || clusterApiUrl(network),
        [network],
    );

    // Empty array = auto-detect all Wallet Standard wallets
    const wallets = useMemo(() => [], []);

    return (
        <ConnectionProvider endpoint={endpoint}>
            <WalletProvider wallets={wallets} autoConnect>
                <WalletModalProvider>{children}</WalletModalProvider>
            </WalletProvider>
        </ConnectionProvider>
    );
}
